import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert, Badge, Button, Card, Center, Divider, Group, Loader, Modal, Pagination, Select, SimpleGrid, Stack, Text, Textarea, Title } from '@mantine/core';
import { IconAlertCircle } from '@tabler/icons-react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

interface Applicant {
  id: number;
  job: number;
  job_title: string;
  applicant_username: string;
  cover_letter: string;
  resume_url?: string;
  status: string;
  applied_at: string;
  is_withdrawn?: boolean;
  modified_by_applicant?: boolean;
  career?: string;
  career_progress?: string;
  private_note?: string;
  job_requires_at_title?: boolean;
}

interface JobSummary {
  id: number;
  title: string;
  last_bulk_download_at?: string | null;
}

const ApplicantsPage: React.FC = () => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const jobId = id ? Number(id) : null;
  const [applicants, setApplicants] = useState<Applicant[]>([]);
  const [jobs, setJobs] = useState<JobSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [accessDenied, setAccessDenied] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [statusCandidate, setStatusCandidate] = useState<Applicant | null>(null);
  const [statusSelection, setStatusSelection] = useState<string | null>(null);
  const [updating, setUpdating] = useState(false);
  const [statusFilters, setStatusFilters] = useState<Record<number, string>>({});
  const [pageByJob, setPageByJob] = useState<Record<number, number>>({});
  const [expandedApplicantId, setExpandedApplicantId] = useState<number | null>(null);
  const [noteDrafts, setNoteDrafts] = useState<Record<number, string>>({});
  const [savingNoteId, setSavingNoteId] = useState<number | null>(null);
  const [jobFilter, setJobFilter] = useState('ALL');
  const [downloadingJobId, setDownloadingJobId] = useState<number | null>(null);
  const pageSize = jobId ? 10 : 5;

  const statusOptions = [
    { value: 'ALL', label: t('applications.filters.all') },
    { value: 'PENDING', label: t('applications.statusPending') },
    { value: 'REVIEWED', label: t('applications.statusReviewed') },
    { value: 'SHORTLISTED', label: t('applications.statusShortlisted') },
    { value: 'REJECTED', label: t('applications.statusRejected') },
    { value: 'ACCEPTED', label: t('applications.statusAccepted') },
    { value: 'WITHDRAWN', label: t('applications.withdrawnShort') },
  ];

  const loadApplicants = useCallback(async () => {
    try {
      setLoading(true);
      setLoadError(null);
      setAccessDenied(false);
      const response = jobId
        ? await api.getJobApplications(jobId)
        : await api.getApplicationsAsJobPoster();
      // Handle paginated response - get applications to user's posted jobs
      const appData = Array.isArray(response) ? response : response.results || [];
      setApplicants(appData);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load applicants';
      if (jobId && (message.includes('permission') || message.includes('403'))) {
        setAccessDenied(true);
        setLoadError(t('common.accessDenied'));
        return;
      }
      setLoadError(message);
    } finally {
      setLoading(false);
    }
  }, [jobId, t]);

  const loadJobs = useCallback(async () => {
    try {
      const response = await api.getMyJobs();
      const jobsData = response;
      setJobs(jobsData.map((job: any) => ({
        id: job.id,
        title: job.title,
        last_bulk_download_at: job.last_bulk_download_at || null,
      })));
    } catch (err) {
      console.error('Failed to load jobs for filter:', err);
    }
  }, []);

  useEffect(() => {
    loadApplicants();
    loadJobs();
  }, [loadApplicants, loadJobs]);

  const handleDownloadZip = async (targetJobId: number) => {
    try {
      setDownloadingJobId(targetJobId);
      setActionError(null);
      const { blob, filename } = await api.downloadJobApplicationsZip(targetJobId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename || `cvs-${targetJobId}.zip`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setJobs((prev) =>
        prev.map((job) =>
          job.id === targetJobId
            ? { ...job, last_bulk_download_at: new Date().toISOString() }
            : job
        )
      );
    } catch (err) {
      if (err instanceof Error) {
        if (err.message.includes('No resumes available') || err.message.includes('Not found')) {
          setActionError(t('postulations.noCvsAvailable'));
        } else {
          setActionError(err.message);
        }
      } else {
        setActionError('Failed to download resumes');
      }
    } finally {
      setDownloadingJobId(null);
    }
  };

  const formatDateTime = (value?: string | null) => {
    if (!value) return null;
    return new Date(value).toLocaleString();
  };

  const handleStatusUpdate = async (applicantId: number, newStatus: string) => {
    try {
      setUpdating(true);
      setActionError(null);
      // Update the status via API
      await api.updateApplicationStatus(applicantId, newStatus);

      // Update local state
      setApplicants((prev) =>
        prev.map((app) => (app.id === applicantId ? { ...app, status: newStatus } : app))
      );

    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to update status');
    } finally {
      setUpdating(false);
    }
  };

  const handleToggleApplicant = (app: Applicant) => {
    setExpandedApplicantId((prev) => (prev === app.id ? null : app.id));
    setNoteDrafts((prev) => ({
      ...prev,
      [app.id]: prev[app.id] ?? app.private_note ?? '',
    }));
  };

  const handleOpenStatus = (app: Applicant) => {
    setStatusCandidate(app);
    setStatusSelection(app.status);
    setStatusModalOpen(true);
  };

  const handleStatusSave = async () => {
    if (!statusCandidate || !statusSelection) return;
    await handleStatusUpdate(statusCandidate.id, statusSelection);
    setStatusModalOpen(false);
    setStatusCandidate(null);
    setStatusSelection(null);
  };

  const handleSaveNote = async (app: Applicant) => {
    try {
      setSavingNoteId(app.id);
      setActionError(null);
      const note = (noteDrafts[app.id] || '').trim();
      const updated = await api.updateApplicationNote(app.id, note);
      setApplicants((prev) =>
        prev.map((item) => (item.id === app.id ? { ...item, private_note: updated.private_note } : item))
      );
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to save note');
    } finally {
      setSavingNoteId(null);
    }
  };

  const progressLabelMap: Record<string, string> = {
    INITIAL: t('applications.progress.initial'),
    INTERMEDIATE: t('applications.progress.intermediate'),
    ADVANCED: t('applications.progress.advanced'),
  };

  const statusLabelMap: Record<string, string> = {
    PENDING: t('applications.statusPending'),
    REVIEWED: t('applications.statusReviewed'),
    SHORTLISTED: t('applications.statusShortlisted'),
    REJECTED: t('applications.statusRejected'),
    ACCEPTED: t('applications.statusAccepted'),
  };

  useEffect(() => {
    if (accessDenied) {
      const timer = window.setTimeout(() => {
        navigate('/jobs');
      }, 1500);
      return () => window.clearTimeout(timer);
    }
  }, [accessDenied, navigate]);

  const canViewApplicants =
    user?.user_type === 'JOB_POSTER' ||
    user?.user_type === 'ADMIN' ||
    user?.user_type === 'MODERATOR';
  const canUpdateStatus =
    user?.user_type === 'JOB_POSTER' || user?.user_type === 'ADMIN';

  if (!canViewApplicants) {
    return (
      <Card withBorder padding="xl" radius="lg" shadow="sm">
        <Title order={3}>{t('common.accessRestricted')}</Title>
        <Text c="dimmed">{t('postulations.onlyPosters')}</Text>
      </Card>
    );
  }

  if (loading) {
    return (
      <Center py="xl">
        <Loader />
      </Center>
    );
  }

  if (loadError) {
    return (
      <Card withBorder padding="xl" radius="lg" shadow="sm">
        <Stack gap="md">
          <Alert icon={<IconAlertCircle size={16} />} title="Error" color="red" radius="md">
            {loadError}
          </Alert>
        </Stack>
      </Card>
    );
  }

  return (
    <Card withBorder padding="xl" radius="lg" shadow="sm">
      <Stack gap="md">
        <div>
          <Title order={2}>{t('postulations.title')}</Title>
          <Text c="dimmed">{t('postulations.subtitle')}</Text>
        </div>
        {actionError && (
          <Alert icon={<IconAlertCircle size={16} />} title="Error" color="red" radius="md">
            {actionError}
          </Alert>
        )}
        {!jobId && jobs.length > 0 && (
          <Group justify="space-between" align="center" wrap="wrap">
            <Select
              label={t('postulations.filterJobLabel')}
              placeholder={t('postulations.filterJobPlaceholder')}
              data={[
                { value: 'ALL', label: t('postulations.filterAllJobs') },
                ...jobs.map((job) => ({ value: String(job.id), label: job.title })),
              ]}
              value={jobFilter}
              onChange={(value) => setJobFilter(value || 'ALL')}
              style={{ width: '100%', maxWidth: 320 }}
            />
          </Group>
        )}

        {applicants.length === 0 ? (
          <Text c="dimmed" ta="center" py="xl">
            {jobId ? t('postulations.emptyForJob') : t('postulations.empty')}
          </Text>
        ) : (
          <Stack gap="md">
            {Object.entries(
              applicants.reduce<Record<number, { job_title: string; items: Applicant[] }>>(
                (acc, app) => {
                  if (!acc[app.job]) {
                    acc[app.job] = { job_title: app.job_title, items: [] };
                  }
                  acc[app.job].items.push(app);
                  return acc;
                },
                {}
              )
            )
              .filter(([entryJobId]) => jobFilter === 'ALL' || entryJobId === jobFilter)
              .map(([jobId, group]) => (
                <Card key={jobId} withBorder radius="md" shadow="xs" padding="md">
                  <Stack gap="sm">
                    <Group justify="space-between" align="center" wrap="wrap">
                      <Title order={5}>{group.job_title}</Title>
                      <Group gap="xs" wrap="wrap" align="flex-start">
                        <Select
                          label={t('applications.filters.status')}
                          data={statusOptions}
                          value={statusFilters[Number(jobId)] || 'ALL'}
                          onChange={(value) =>
                            setStatusFilters((prev) => ({
                              ...prev,
                              [Number(jobId)]: value || 'ALL',
                            }))
                          }
                          style={{ width: '100%', maxWidth: 220 }}
                        />
                        {group.items.some((app) => Boolean(app.resume_url)) ? (
                          <Button
                            variant="light"
                            size="sm"
                            onClick={() => handleDownloadZip(Number(jobId))}
                            loading={downloadingJobId === Number(jobId)}
                          >
                            {t('postulations.downloadZip')}
                          </Button>
                        ) : (
                          <Text c="dimmed" size="xs">
                            {t('postulations.noCvsAvailable')}
                          </Text>
                        )}
                      </Group>
                    </Group>
                    {(() => {
                      const lastDate = formatDateTime(
                        jobs.find((item) => item.id === Number(jobId))?.last_bulk_download_at
                      );
                      return (
                        <Text c="dimmed" size="xs">
                          {lastDate
                            ? t('postulations.lastBulkDownload', { date: lastDate })
                            : t('postulations.noBulkDownload')}
                        </Text>
                      );
                    })()}
                    <Stack gap="sm">
                      {group.items
                        .filter((app) => {
                          const filter = statusFilters[app.job] || 'ALL';
                          if (filter === 'ALL') return true;
                          if (filter === 'WITHDRAWN') return Boolean(app.is_withdrawn);
                          return app.status === filter && !app.is_withdrawn;
                        })
                        .slice(
                          ((pageByJob[Number(jobId)] || 1) - 1) * pageSize,
                          (pageByJob[Number(jobId)] || 1) * pageSize
                        )
                        .map((app) => (
                          <Card key={app.id} withBorder radius="md" shadow="xs" padding="md">
                            <Group justify="space-between" align="flex-start" wrap="wrap">
                              <div>
                                <Text size="sm" fw={500}>{app.applicant_username}</Text>
                                <Badge size="sm" variant="light" color="orange" mt="xs">
                                  {t('postulations.applied')}: {new Date(app.applied_at).toLocaleDateString()}
                                </Badge>
                                {(app.career || app.career_progress || app.job_requires_at_title) && (
                                  <Group gap="xs" mt="xs" wrap="wrap">
                                    {app.career && (
                                      <Badge size="sm" variant="light" color="orange">
                                        {app.career}
                                      </Badge>
                                    )}
                                    {app.career_progress && (
                                      <Badge size="sm" variant="light" color="orange">
                                        {progressLabelMap[app.career_progress] || app.career_progress}
                                      </Badge>
                                    )}
                                    {app.job_requires_at_title && (
                                      <Badge size="sm" variant="light" color="orange">
                                        {t('postulations.atTag')}
                                      </Badge>
                                    )}
                                  </Group>
                                )}
                              </div>
                              <Group gap="xs" align="flex-start" wrap="wrap">
                                <Badge variant="light" color={app.is_withdrawn ? 'gray' : 'orange'}>
                                  {app.is_withdrawn
                                    ? t('applications.withdrawnShort')
                                    : (statusLabelMap[app.status] || app.status)}
                                </Badge>
                                {app.modified_by_applicant && !app.is_withdrawn && (
                                  <Badge variant="light" color="orange">
                                    {t('applications.modifiedShort')}
                                  </Badge>
                                )}
                                {app.resume_url && (
                                  <Button
                                    variant="light"
                                    size="sm"
                                    component="a"
                                    href={app.resume_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    {t('jobDetail.download')}
                                  </Button>
                                )}
                                {!app.is_withdrawn && canUpdateStatus && (
                                  <Button
                                    variant="light"
                                    size="sm"
                                    onClick={() => handleOpenStatus(app)}
                                  >
                                    {t('applications.markReview')}
                                  </Button>
                                )}
                                <Button
                                  variant="light"
                                  size="sm"
                                  onClick={() => handleToggleApplicant(app)}
                                >
                                  {expandedApplicantId === app.id
                                    ? t('applications.minimize')
                                    : t('applications.viewFull')}
                                </Button>
                              </Group>
                            </Group>
                            {expandedApplicantId === app.id && (
                              <>
                                <Divider my="sm" />
                                <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
                                  <Stack gap="sm">
                                    <div>
                                      <Text fw={500} mb="xs">{t('applications.privateNoteLabel')}</Text>
                                      <Textarea
                                        value={noteDrafts[app.id] ?? app.private_note ?? ''}
                                        onChange={(event) => {
                                          const value = event.currentTarget.value;
                                          setNoteDrafts((prev) => ({
                                            ...prev,
                                            [app.id]: value,
                                          }));
                                        }}
                                        placeholder={t('applications.privateNotePlaceholder')}
                                        maxLength={100}
                                        minRows={3}
                                        description={`${(noteDrafts[app.id] ?? app.private_note ?? '').length}/100`}
                                      />
                                      <Group justify="flex-end" mt="xs" wrap="wrap">
                                        <Button
                                          size="xs"
                                          onClick={() => handleSaveNote(app)}
                                          loading={savingNoteId === app.id}
                                          disabled={(noteDrafts[app.id] ?? app.private_note ?? '') === (app.private_note ?? '')}
                                        >
                                          {t('applications.privateNoteSave')}
                                        </Button>
                                      </Group>
                                    </div>
                                    {app.is_withdrawn && (
                                      <Text c="dimmed" size="sm">{t('applications.withdrawnByApplicant')}</Text>
                                    )}
                                  </Stack>
                                  <Stack gap="sm">
                                    {app.cover_letter && (
                                      <div>
                                        <Text fw={500} mb="xs">{t('jobDetail.coverLetter')}</Text>
                                        <Text size="sm">{app.cover_letter}</Text>
                                      </div>
                                    )}
                                  </Stack>
                                </SimpleGrid>
                              </>
                            )}
                          </Card>
                        ))}
                      {group.items.filter((app) => {
                        const filter = statusFilters[app.job] || 'ALL';
                        if (filter === 'ALL') return true;
                        if (filter === 'WITHDRAWN') return Boolean(app.is_withdrawn);
                        return app.status === filter && !app.is_withdrawn;
                      }).length > pageSize && (
                          <Group justify="center" mt="xs">
                            <Pagination
                              value={pageByJob[Number(jobId)] || 1}
                              onChange={(value) =>
                                setPageByJob((prev) => ({
                                  ...prev,
                                  [Number(jobId)]: value,
                                }))
                              }
                              total={Math.max(
                                1,
                                Math.ceil(
                                  group.items.filter((app) => {
                                    const filter = statusFilters[app.job] || 'ALL';
                                    if (filter === 'ALL') return true;
                                    if (filter === 'WITHDRAWN') return Boolean(app.is_withdrawn);
                                    return app.status === filter && !app.is_withdrawn;
                                  }).length / pageSize
                                )
                              )}
                            />
                          </Group>
                        )}
                    </Stack>
                  </Stack>
                </Card>
              ))}
          </Stack>
        )}
      </Stack>
      <Modal
        opened={statusModalOpen}
        onClose={() => setStatusModalOpen(false)}
        title={t('applications.statusTitle')}
        size="sm"
      >
        <Stack gap="md">
          <Select
            label={t('applications.statusLabel')}
            data={statusOptions.filter((option) => option.value !== 'ALL' && option.value !== 'WITHDRAWN')}
            value={statusSelection}
            onChange={setStatusSelection}
          />
          <Group justify="flex-end" gap="sm">
            <Button variant="subtle" onClick={() => setStatusModalOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handleStatusSave}
              loading={updating}
              disabled={!statusSelection}
            >
              {t('applications.statusSave')}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Card>
  );
};

export default ApplicantsPage;
